/**
 * FinancialDetailModal — NPV / ROI / Break-Even detail analysis with editable inputs.
 *
 * Opens from clickable cells in InvestmentTable. Shows formula breakdown,
 * editable input fields that write back to the node, and live recalculation.
 * Includes an AI Insights placeholder for future platform integration.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import { useAdjacencyStore } from '../../store/adjacency-store';
import { computeNpv, formatCurrency } from '../../utils/financial-calculations';

type Mode = 'npv' | 'roi' | 'breakeven';

interface FinancialDetailModalProps {
  node: AdjacencyNode;
  mode: Mode;
  discountRate: number;
  periodYears: number;
  onClose: () => void;
}

const TABS: { key: Mode; label: string }[] = [
  { key: 'npv', label: 'NPV' },
  { key: 'roi', label: 'ROI' },
  { key: 'breakeven', label: 'Break-Even' },
];

export function FinancialDetailModal({
  node,
  mode: initialMode,
  discountRate,
  periodYears,
  onClose,
}: FinancialDetailModalProps) {
  const updateNode = useAdjacencyStore((s) => s.updateNode);
  const updateResourceConfig = useAdjacencyStore((s) => s.updateResourceConfig);
  const [mode, setMode] = useState<Mode>(initialMode);

  // Local editable copies of node financial data for live preview
  const [iRnD, setIRnD] = useState(node.iRnD ?? 0);
  const [fRnD, setFRnD] = useState(node.fRnD ?? 0);
  const [estRevenue, setEstRevenue] = useState(node.estRevenue ?? 0);
  const [localRate, setLocalRate] = useState(discountRate);
  const [localPeriod, setLocalPeriod] = useState(periodYears);

  // Sync local rate/period with store changes
  useEffect(() => { setLocalRate(discountRate); }, [discountRate]);
  useEffect(() => { setLocalPeriod(periodYears); }, [periodYears]);

  // Derived values
  const totalInvestment = iRnD + fRnD;

  const roi = useMemo(() => {
    if (estRevenue > 0 && totalInvestment > 0) {
      return ((estRevenue - totalInvestment) / totalInvestment) * 100;
    }
    return null;
  }, [estRevenue, totalInvestment]);

  const breakEvenYears = useMemo(() => {
    if (estRevenue > 0 && totalInvestment > 0) {
      return totalInvestment / estRevenue;
    }
    return null;
  }, [estRevenue, totalInvestment]);

  const npv = useMemo(() => {
    if (estRevenue > 0 && totalInvestment > 0) {
      return computeNpv(totalInvestment, estRevenue, localRate, localPeriod);
    }
    return null;
  }, [estRevenue, totalInvestment, localRate, localPeriod]);

  // Year-by-year breakdown for NPV tab
  const yearBreakdown = useMemo(() => {
    const rows: { year: number; revenue: number; discountFactor: number; pv: number }[] = [];
    let totalPv = 0;
    for (let t = 1; t <= localPeriod; t++) {
      const factor = 1 / Math.pow(1 + localRate, t);
      const pv = estRevenue * factor;
      totalPv += pv;
      rows.push({ year: t, revenue: estRevenue, discountFactor: factor, pv });
    }
    return { rows, totalPv };
  }, [estRevenue, localRate, localPeriod]);

  // Write back to store on field change
  const commitNodeField = useCallback(
    (field: 'iRnD' | 'fRnD' | 'estRevenue', value: number) => {
      updateNode(node.id, { [field]: value || null });
    },
    [updateNode, node.id],
  );

  const commitRate = useCallback(
    (rate: number) => {
      updateResourceConfig({ npvDiscountRate: rate });
    },
    [updateResourceConfig],
  );

  const commitPeriod = useCallback(
    (years: number) => {
      updateResourceConfig({ npvPeriodYears: years });
    },
    [updateResourceConfig],
  );

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center py-6"
      style={{ background: 'var(--modal-backdrop-heavy)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="glass-panel rounded-xl p-6 max-w-[560px] w-full mx-4 max-h-[calc(100vh-48px)] overflow-y-auto"
        style={{
          background: 'var(--modal-bg-heavy)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid var(--modal-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold truncate pr-2" style={{ color: 'var(--text-primary)' }}>
            Financial Analysis — {node.name}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 transition-colors shrink-0"
            style={{ color: 'var(--text-muted)' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-4 rounded-lg p-1" style={{ background: 'var(--glass-bg-subtle)' }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setMode(tab.key)}
              className="flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              style={{
                background: mode === tab.key ? 'rgba(59,130,246,0.2)' : 'transparent',
                color: mode === tab.key ? '#60a5fa' : 'var(--text-muted)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {mode === 'npv' && (
          <NpvTab
            iRnD={iRnD} fRnD={fRnD} estRevenue={estRevenue}
            totalInvestment={totalInvestment}
            localRate={localRate} localPeriod={localPeriod}
            npv={npv} yearBreakdown={yearBreakdown}
            setIRnD={setIRnD} setFRnD={setFRnD} setEstRevenue={setEstRevenue}
            setLocalRate={setLocalRate} setLocalPeriod={setLocalPeriod}
            commitNodeField={commitNodeField}
            commitRate={commitRate} commitPeriod={commitPeriod}
          />
        )}
        {mode === 'roi' && (
          <RoiTab
            iRnD={iRnD} fRnD={fRnD} estRevenue={estRevenue}
            totalInvestment={totalInvestment} roi={roi}
            setIRnD={setIRnD} setFRnD={setFRnD} setEstRevenue={setEstRevenue}
            commitNodeField={commitNodeField}
          />
        )}
        {mode === 'breakeven' && (
          <BreakEvenTab
            iRnD={iRnD} fRnD={fRnD} estRevenue={estRevenue}
            totalInvestment={totalInvestment} breakEvenYears={breakEvenYears}
            setIRnD={setIRnD} setFRnD={setFRnD} setEstRevenue={setEstRevenue}
            commitNodeField={commitNodeField}
          />
        )}

        {/* ── AI Insights (Placeholder) ──────────────────────────── */}
        {/* TODO [AI Phase]: Replace with AiInsightsPanel component.
            Integration point: pass node financial data + resourceConfig + portfolio summary
            to the platform AI chatbot. See BUILD_PLAN.md Phase AI for architecture.
            Context: { node, financialMetrics, resourceConfig, portfolioSummary } */}
        <div
          className="mt-4 rounded-lg p-4"
          style={{
            background: 'rgba(217, 119, 6, 0.06)',
            border: '1px dashed rgba(217, 119, 6, 0.25)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} style={{ color: '#d97706' }} />
            <span className="text-xs font-semibold" style={{ color: '#d97706' }}>
              AI Insights
            </span>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full"
              style={{ background: 'rgba(217,119,6,0.15)', color: '#d97706' }}
            >
              Coming Soon
            </span>
          </div>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            {mode === 'npv' && 'AI-powered NPV sensitivity analysis will appear here. Explore how changes in discount rate, revenue timing, or investment phasing affect net present value.'}
            {mode === 'roi' && 'AI-powered ROI optimization suggestions will appear here. Compare against portfolio benchmarks and get recommendations for improving returns.'}
            {mode === 'breakeven' && 'AI-powered break-even risk analysis will appear here. Understand how revenue variability affects time to break-even and get scenario comparisons.'}
          </p>
          <button
            disabled
            className="mt-3 px-3 py-1.5 rounded-lg text-xs font-medium opacity-50 cursor-not-allowed"
            style={{
              background: 'rgba(217,119,6,0.15)',
              color: '#d97706',
              border: '1px solid rgba(217,119,6,0.2)',
            }}
          >
            Ask AI about this analysis
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Editable Input ──────────────────────────────────────────────────

function EditableFinanceInput({
  label,
  value,
  prefix = '$',
  suffix,
  onChange,
  onCommit,
  readOnly = false,
}: {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  onChange: (v: number) => void;
  onCommit: (v: number) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-[11px] shrink-0" style={{ color: 'var(--text-muted)' }}>
        {label}
      </label>
      <div className="inline-flex items-center gap-1">
        {prefix && (
          <span className="text-[11px]" style={{ color: 'var(--text-dim)' }}>{prefix}</span>
        )}
        <input
          type="number"
          value={value}
          readOnly={readOnly}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(v);
          }}
          onBlur={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onCommit(v);
          }}
          className="w-28 px-2 py-1 text-xs text-right rounded tabular-nums"
          style={{
            background: readOnly ? 'var(--glass-bg-subtle)' : 'var(--modal-input-bg)',
            border: `1px solid ${readOnly ? 'var(--glass-border)' : 'var(--modal-input-border)'}`,
            color: readOnly ? 'var(--text-muted)' : 'var(--text-primary)',
            outline: 'none',
          }}
        />
        {suffix && (
          <span className="text-[11px]" style={{ color: 'var(--text-dim)' }}>{suffix}</span>
        )}
      </div>
    </div>
  );
}

// ── NPV Tab ─────────────────────────────────────────────────────────

function NpvTab({
  iRnD, fRnD, estRevenue, totalInvestment,
  localRate, localPeriod, npv, yearBreakdown,
  setIRnD, setFRnD, setEstRevenue,
  setLocalRate, setLocalPeriod,
  commitNodeField, commitRate, commitPeriod,
}: {
  iRnD: number; fRnD: number; estRevenue: number; totalInvestment: number;
  localRate: number; localPeriod: number; npv: number | null;
  yearBreakdown: { rows: { year: number; revenue: number; discountFactor: number; pv: number }[]; totalPv: number };
  setIRnD: (v: number) => void; setFRnD: (v: number) => void; setEstRevenue: (v: number) => void;
  setLocalRate: (v: number) => void; setLocalPeriod: (v: number) => void;
  commitNodeField: (field: 'iRnD' | 'fRnD' | 'estRevenue', v: number) => void;
  commitRate: (v: number) => void; commitPeriod: (v: number) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Formula */}
      <div className="rounded-lg p-3 font-mono text-xs" style={{ background: 'var(--modal-formula-bg)', color: 'var(--text-secondary)' }}>
        NPV = -Investment + &Sigma; (Annual Revenue / (1 + r)<sup>t</sup>) &nbsp; for t=1 to N
      </div>

      {/* Inputs */}
      <div className="space-y-2">
        <EditableFinanceInput label="iR&D" value={iRnD} onChange={setIRnD} onCommit={(v) => commitNodeField('iRnD', v)} />
        <EditableFinanceInput label="FR&D" value={fRnD} onChange={setFRnD} onCommit={(v) => commitNodeField('fRnD', v)} />
        <EditableFinanceInput label="Total Investment" value={totalInvestment} onChange={() => {}} onCommit={() => {}} readOnly />
        <EditableFinanceInput label="Est. Annual Revenue" value={estRevenue} onChange={setEstRevenue} onCommit={(v) => commitNodeField('estRevenue', v)} />
        <EditableFinanceInput
          label="Discount Rate" prefix="%"
          value={Math.round(localRate * 100 * 10) / 10}
          onChange={(v) => setLocalRate(v / 100)}
          onCommit={(v) => commitRate(v / 100)}
        />
        <div className="flex items-center justify-between gap-3">
          <label className="text-[11px] shrink-0" style={{ color: 'var(--text-muted)' }}>
            Period (years)
          </label>
          <select
            value={localPeriod}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              setLocalPeriod(v);
              commitPeriod(v);
            }}
            className="w-28 px-2 py-1 text-xs text-right rounded"
            style={{
              background: 'var(--modal-input-bg)',
              border: '1px solid var(--modal-input-border)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          >
            <option value={3}>3 years</option>
            <option value={5}>5 years</option>
            <option value={7}>7 years</option>
            <option value={10}>10 years</option>
          </select>
        </div>
      </div>

      {/* Year-by-year breakdown */}
      {estRevenue > 0 && totalInvestment > 0 && (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th className="px-2 py-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Year</th>
                <th className="px-2 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Revenue</th>
                <th className="px-2 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Discount Factor</th>
                <th className="px-2 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Present Value</th>
              </tr>
            </thead>
            <tbody>
              {yearBreakdown.rows.map((r) => (
                <tr key={r.year} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <td className="px-2 py-1 tabular-nums" style={{ color: 'var(--text-secondary)' }}>{r.year}</td>
                  <td className="px-2 py-1 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>{formatCurrency(r.revenue)}</td>
                  <td className="px-2 py-1 tabular-nums text-right" style={{ color: 'var(--text-dim)' }}>
                    1/(1+{(localRate * 100).toFixed(0)}%)<sup>{r.year}</sup> = {r.discountFactor.toFixed(4)}
                  </td>
                  <td className="px-2 py-1 tabular-nums text-right" style={{ color: 'var(--text-primary)' }}>{formatCurrency(r.pv)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid rgba(59,130,246,0.2)', background: 'rgba(59,130,246,0.05)' }}>
                <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--text-primary)' }} colSpan={3}>Total PV</td>
                <td className="px-2 py-1.5 font-bold tabular-nums text-right" style={{ color: 'var(--text-primary)' }}>{formatCurrency(yearBreakdown.totalPv)}</td>
              </tr>
              <tr style={{ background: 'rgba(59,130,246,0.05)' }}>
                <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }} colSpan={3}>– Investment</td>
                <td className="px-2 py-1 tabular-nums text-right" style={{ color: '#ef4444' }}>–{formatCurrency(totalInvestment)}</td>
              </tr>
              <tr style={{ background: 'rgba(59,130,246,0.08)' }}>
                <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--text-primary)' }} colSpan={3}>= NPV</td>
                <td className="px-2 py-1.5 font-bold tabular-nums text-right" style={{ color: npv != null && npv >= 0 ? '#22c55e' : '#ef4444' }}>
                  {npv != null ? formatCurrency(npv) : '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Result box */}
      <ResultBox
        label="Net Present Value"
        value={npv != null ? formatCurrency(npv) : '—'}
        color={npv != null ? (npv >= 0 ? '#22c55e' : '#ef4444') : 'var(--text-muted)'}
      />
    </div>
  );
}

// ── ROI Tab ─────────────────────────────────────────────────────────

function RoiTab({
  iRnD, fRnD, estRevenue, totalInvestment, roi,
  setIRnD, setFRnD, setEstRevenue, commitNodeField,
}: {
  iRnD: number; fRnD: number; estRevenue: number; totalInvestment: number; roi: number | null;
  setIRnD: (v: number) => void; setFRnD: (v: number) => void; setEstRevenue: (v: number) => void;
  commitNodeField: (field: 'iRnD' | 'fRnD' | 'estRevenue', v: number) => void;
}) {
  const netGain = estRevenue - totalInvestment;

  return (
    <div className="space-y-4">
      {/* Formula */}
      <div className="rounded-lg p-3 font-mono text-xs" style={{ background: 'var(--modal-formula-bg)', color: 'var(--text-secondary)' }}>
        ROI = (Est Revenue - Total Investment) / Total Investment &times; 100%
      </div>

      {/* Inputs */}
      <div className="space-y-2">
        <EditableFinanceInput label="iR&D" value={iRnD} onChange={setIRnD} onCommit={(v) => commitNodeField('iRnD', v)} />
        <EditableFinanceInput label="FR&D" value={fRnD} onChange={setFRnD} onCommit={(v) => commitNodeField('fRnD', v)} />
        <EditableFinanceInput label="Total Investment" value={totalInvestment} onChange={() => {}} onCommit={() => {}} readOnly />
        <EditableFinanceInput label="Est. Annual Revenue" value={estRevenue} onChange={setEstRevenue} onCommit={(v) => commitNodeField('estRevenue', v)} />
      </div>

      {/* Calculation breakdown */}
      {totalInvestment > 0 && estRevenue > 0 && (
        <div className="rounded-lg p-3 space-y-1 text-xs font-mono" style={{ background: 'var(--modal-formula-bg)', color: 'var(--text-secondary)' }}>
          <div>Net Gain = {formatCurrency(estRevenue)} - {formatCurrency(totalInvestment)} = <span style={{ color: netGain >= 0 ? '#22c55e' : '#ef4444' }}>{formatCurrency(netGain)}</span></div>
          <div>ROI = {formatCurrency(netGain)} / {formatCurrency(totalInvestment)} &times; 100 = <span style={{ color: roi != null && roi >= 0 ? '#22c55e' : '#ef4444' }}>{roi != null ? `${roi.toFixed(1)}%` : '—'}</span></div>
        </div>
      )}

      {/* Result box */}
      <ResultBox
        label="Return on Investment"
        value={roi != null ? `${roi.toFixed(1)}%` : '—'}
        color={roi != null ? (roi >= 0 ? '#22c55e' : '#ef4444') : 'var(--text-muted)'}
      />
    </div>
  );
}

// ── Break-Even Tab ──────────────────────────────────────────────────

function BreakEvenTab({
  iRnD, fRnD, estRevenue, totalInvestment, breakEvenYears,
  setIRnD, setFRnD, setEstRevenue, commitNodeField,
}: {
  iRnD: number; fRnD: number; estRevenue: number; totalInvestment: number; breakEvenYears: number | null;
  setIRnD: (v: number) => void; setFRnD: (v: number) => void; setEstRevenue: (v: number) => void;
  commitNodeField: (field: 'iRnD' | 'fRnD' | 'estRevenue', v: number) => void;
}) {
  const maxYears = Math.max(5, breakEvenYears != null ? Math.ceil(breakEvenYears) + 1 : 5);
  const pct = breakEvenYears != null ? Math.min((breakEvenYears / maxYears) * 100, 100) : 0;
  const beColor = breakEvenYears != null
    ? breakEvenYears < 3 ? '#22c55e' : breakEvenYears <= 5 ? '#eab308' : '#ef4444'
    : 'var(--text-muted)';

  return (
    <div className="space-y-4">
      {/* Formula */}
      <div className="rounded-lg p-3 font-mono text-xs" style={{ background: 'var(--modal-formula-bg)', color: 'var(--text-secondary)' }}>
        Break-Even = Total Investment / Est. Annual Revenue
      </div>

      {/* Inputs */}
      <div className="space-y-2">
        <EditableFinanceInput label="iR&D" value={iRnD} onChange={setIRnD} onCommit={(v) => commitNodeField('iRnD', v)} />
        <EditableFinanceInput label="FR&D" value={fRnD} onChange={setFRnD} onCommit={(v) => commitNodeField('fRnD', v)} />
        <EditableFinanceInput label="Total Investment" value={totalInvestment} onChange={() => {}} onCommit={() => {}} readOnly />
        <EditableFinanceInput label="Est. Annual Revenue" value={estRevenue} onChange={setEstRevenue} onCommit={(v) => commitNodeField('estRevenue', v)} />
      </div>

      {/* Visual timeline bar */}
      {breakEvenYears != null && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>Break-Even Timeline</div>
          <div className="relative h-6 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            {/* Filled bar */}
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all"
              style={{ width: `${pct}%`, background: `${beColor}30` }}
            />
            {/* Marker */}
            <div
              className="absolute top-0 bottom-0 w-0.5"
              style={{ left: `${pct}%`, background: beColor }}
            />
            {/* Year labels */}
            <div className="absolute inset-0 flex items-center justify-between px-2 text-[9px] tabular-nums" style={{ color: 'var(--text-dim)' }}>
              <span>0</span>
              {Array.from({ length: maxYears }, (_, i) => i + 1).map((y) => (
                <span key={y}>{y}yr</span>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-center" style={{ color: beColor }}>
            Break-even at {breakEvenYears.toFixed(1)} years
          </div>
        </div>
      )}

      {/* Result box */}
      <ResultBox
        label="Break-Even Point"
        value={breakEvenYears != null ? `${breakEvenYears.toFixed(1)} years` : '—'}
        color={beColor}
      />
    </div>
  );
}

// ── Result Box ──────────────────────────────────────────────────────

function ResultBox({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      className="rounded-lg p-4 text-center"
      style={{ background: `${color}08`, border: `1px solid ${color}25` }}
    >
      <div className="text-[11px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
